import { Suspense } from 'react';
import { Await } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import { CategoryNavLink } from '@/components/category-nav-link';

function collectDeferredNavCategories(
    enrichedCategories: ShopperProducts.schemas['Category'][]
): ShopperProducts.schemas['Category'][] {
    const links: ShopperProducts.schemas['Category'][] = [];

    const visit = (items: ShopperProducts.schemas['Category'][] | undefined) => {
        const categories = items ?? [];
        for (const category of categories) {
            if (!category.c_showInMenu) {
                continue;
            }

            links.push(category);
            visit(category.categories);
        }
    };

    // Root-level categories already have `<a href>` in the desktop and mobile menus. Nested targets (deeper
    // categories, pages, content assets, folders) are not in the initial SSR HTML — desktop panels mount on
    // interaction and mobile sub-trees render on expand — so we emit only descendants from the defer payload.
    for (const enrichedCategory of enrichedCategories) {
        visit(enrichedCategory.categories);
    }

    return links;
}

function NavigationSeoLinksView({ enrichedCategories }: { enrichedCategories: ShopperProducts.schemas['Category'][] }) {
    const { t } = useTranslation('header');
    const links = collectDeferredNavCategories(enrichedCategories);

    if (links.length === 0) {
        return null;
    }

    return (
        <nav aria-label={t('seoNavigationLinks', 'Site navigation links')} className="sr-only">
            <ul>
                {links.map((category) => (
                    <li key={category.id}>
                        <CategoryNavLink category={category} />
                    </li>
                ))}
            </ul>
        </nav>
    );
}

/**
 * Renders a screen-reader-only flat link list once deferred subcategory data resolves.
 *
 * Categories are filtered with `c_showInMenu`, aligned with the default flyout validation and visible menu filtering.
 * Keeps nested flyout targets in the SSR HTML for crawlers without force-mounting Radix dropdown panels, which
 * breaks viewport scoping on desktop.
 */
export function NavigationSeoLinks({
    defer: subCategoriesPromise,
}: {
    defer?: Promise<ShopperProducts.schemas['Category'][]>;
}) {
    if (!subCategoriesPromise) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <Await resolve={subCategoriesPromise}>
                {(enrichedCategories) => <NavigationSeoLinksView enrichedCategories={enrichedCategories} />}
            </Await>
        </Suspense>
    );
}
